import React, { useState, useRef, useEffect } from 'react';
import { FaBars } from 'react-icons/fa';
import { MdClose, MdLogout, MdOutlineBedroomParent, MdOutlineHelpOutline } from 'react-icons/md';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../common/AuthProvider';
import { RiBillLine } from 'react-icons/ri';
import { IoMdPerson, IoMdCheckmarkCircleOutline } from 'react-icons/io';
import Login from './LoginPage';
import hotelLogo from '../assets/logo.png';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Cache constants
const USER_DATA_CACHE_KEY = 'userDataCache';
const PROPERTY_CACHE_KEY = 'hotelPropertyCache';
const CACHE_EXPIRY_MS = 3600000; // 1 hour

const getGuestIdFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
};

const decryptUserData = async (encryptedData) => {
  try {
    if (encryptedData.email && encryptedData.email.length < 50) {
      return encryptedData;
    }

    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        encrypted_fields: {
          email: encryptedData.email,
          phone_number: encryptedData.phone_number,
          country_code: encryptedData.country_code,
        },
      }),
    });

    if (response.status === 401) {
      return encryptedData;
    }

    if (!response.ok) {
      throw new Error(`Decryption failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData;
  }
};

const getGuestData = async () => {
  const accessToken = Cookies.get('access_token');
  if (!accessToken) return null;

  try {
    const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
    if (!guestId) return null;

    const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!profileResponse.ok) {
      throw new Error(`Profile API Error: ${profileResponse.statusText}`);
    }

    const profileData = await profileResponse.json();
    const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

    if (isDecrypted) {
      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: profileData.emailid || '',
        country_code: profileData.countrycode,
        phone_number: profileData.phonenumber || '',
      };
    }

    const decryptedFields = await decryptUserData({
      email: profileData.emailid,
      phone_number: profileData.phonenumber,
      country_code: profileData.countrycode,
    });

    return {
      firstname: profileData.firstname || '',
      lastname: profileData.lastname || '',
      email: decryptedFields.email || profileData.emailid || '',
      country_code: profileData.countrycode,
      phone_number: profileData.phonenumber || '',
    };
  } catch (error) {
    console.error('Error fetching guest data:', error);
    return null;
  }
};

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [property, setProperty] = useState({
    name: '',
    location: '',
    logo_url: hotelLogo,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingCache, setIsRefreshingCache] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const dropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const navigate = useNavigate();

  // Helper function to get cached data
  const getCachedData = (key) => {
    const cachedData = localStorage.getItem(key);
    if (!cachedData) return null;

    const { data, timestamp } = JSON.parse(cachedData);
    if (Date.now() - timestamp < CACHE_EXPIRY_MS) {
      return data;
    }
    return null;
  };

  // Fetch user data when authenticated
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated) {
        setIsProfileLoading(false);
        setIsRefreshingCache(false);
        return;
      }

      // Show cached data immediately if available
      const cachedData = getCachedData(USER_DATA_CACHE_KEY);
      if (cachedData) {
        setUserData(cachedData);
      }

      // Always refresh data in background
      try {
        const data = await getGuestData();
        if (data) {
          setUserData(data);
          localStorage.setItem(
            USER_DATA_CACHE_KEY,
            JSON.stringify({
              data,
              timestamp: Date.now(),
            })
          );
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsProfileLoading(false);
        setIsRefreshingCache(false);
      }
    };

    fetchUserData();
  }, [isAuthenticated]);

  // Get user initial
  const getUserInitial = () => {
    if (!isAuthenticated) return 'Login';

    if (isRefreshingCache || isProfileLoading) return '';

    if (userData?.firstname) {
      return userData.firstname.charAt(0).toUpperCase();
    }

    if (user?.firstname) {
      return user.firstname.charAt(0).toUpperCase();
    }

    if (userData?.email) {
      return userData.email.charAt(0).toUpperCase();
    }

    return 'U';
  };

  const fetchProperty = async () => {
    // Show cached data immediately if available
    const cachedData = getCachedData(PROPERTY_CACHE_KEY);
    if (cachedData) {
      setProperty({
        name: cachedData.name || '',
        location: cachedData.location || '',
        logo_url: cachedData.logo_url || hotelLogo,
        city: cachedData.city || '',
      });
    }

    // Always refresh data in background
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/1`);
      if (response.ok) {
        const data = await response.json();
        setProperty({
          name: data.name || '',
          location: data.location || '',
          logo_url: data.logo_url || hotelLogo,
          city: data.city || '',
        });
        localStorage.setItem(
          PROPERTY_CACHE_KEY,
          JSON.stringify({
            data,
            timestamp: Date.now(),
          })
        );
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshingCache(false);
    }
  };

  useEffect(() => {
    fetchProperty();
  }, []);

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      profileButtonRef.current &&
      !profileButtonRef.current.contains(event.target)
    ) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleLogout = async () => {
    // Clear all caches on logout
    localStorage.removeItem(USER_DATA_CACHE_KEY);
    await logout();
    setIsDropdownOpen(false);
  };

  const reservationRoutes = [
    '/profile_guest_options',
    '/walk-in/room-reservation',
    '/enhance-checkin-stay',
    '/walk-in/booking-summary',
    '/payment-succesful',
    '/reservation-succesful',
  ];

  const checkInRoutes = ['/reservation/options', '/checkin-details'];

  const checkOutRoutes = [
    '/check-out-field',
    '/booking-checkin',
    '/checkout-payment-success',
    'check-out-options',
  ];

  const isOnReservationPage = reservationRoutes.some((route) => location.pathname.includes(route));
  const isOnCheckInPage = checkInRoutes.some((route) => location.pathname.includes(route));
  const isOnCheckOutPage = checkOutRoutes.some((route) => location.pathname.includes(route));

  const buildMenuItems = () => {
    const items = [];

    if (isAuthenticated) {
      if (!isOnReservationPage) {
        items.push({
          id: 1,
          text: 'Reservation',
          icon: <MdOutlineBedroomParent />,
          href: '/profile_guest_options',
        });
      }

      if (!isOnCheckInPage) {
        items.push({
          id: 2,
          text: 'Check-In',
          icon: <IoMdCheckmarkCircleOutline />,
          href: '/reservation/options',
        });
      }

      if (!isOnCheckOutPage) {
        items.push({
          id: 3,
          text: 'Check-Out',
          icon: <RiBillLine />,
          href: '/check-out-options',
        });
      }
    }

    items.push({
      id: 4,
      text: 'Need Help',
      icon: <MdOutlineHelpOutline />,
      href: '/need-help',
    });

    return items;
  };

  const menuItems = buildMenuItems();

  const ProfileIcon = () => {
    if (isProfileLoading || isRefreshingCache) {
      return <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse"></div>;
    }

    const initial = getUserInitial();

    return (
      <div
        ref={profileButtonRef}
        className="w-14 h-14 text-md flex items-center justify-center bg-black text-white rounded-full font-bold cursor-pointer hover:bg-gray-800 transition-colors"
        onClick={() => {
          if (isAuthenticated) {
            setIsDropdownOpen(!isDropdownOpen);
          } else {
            setShowLoginModal(true);
          }
        }}
        aria-label={isAuthenticated ? 'User menu' : 'Login'}
      >
        {initial}
      </div>
    );
  };

  if (isLoading || isRefreshingCache) {
    return (
      <header className="w-full bg-transparent fixed top-0 left-0 right-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-5 flex items-center justify-between h-20">
          <div className="h-16 w-48 bg-gray-200 animate-pulse rounded"></div>
          <div className="flex items-center gap-4">
            <div className="w-24 h-8 bg-gray-200 animate-pulse rounded"></div>
            <div className="w-14 h-14 bg-gray-200 animate-pulse rounded-full"></div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header
        className={`w-full fixed top-0 left-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md text-black' : 'bg-gray-50 bg-opacity-40 text-white'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-5 flex items-center justify-between h-20">
          <Link to="/" className="text-xl font-bold flex items-center gap-2">
            <img src={property.logo_url} alt="Hotel Logo" className="h-16 object-center" />
            {isAuthenticated && (
              <div className="flex flex-col">
                <span className="text-black font-poppins text-lg">{property.name}</span>
                <div className="text-gray-800 font-poppins text-sm flex gap-1">
                  <span>{property.city},</span>
                  <span>{property.location}</span>
                </div>
              </div>
            )}
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden md:flex font-poppins space-x-5 cursor-pointer text-black font-semibold text-lg">
            {menuItems.map((item) => (
              <Link
                key={item.id}
                to={item.href}
                className="flex items-center gap-2 hover:scale-105 transition-transform"
              >
                {item.icon}
                {item.text}
              </Link>
            ))}

            <ProfileIcon />
          </nav>

          <button
            onClick={toggleSidebar}
            className="md:hidden flex items-center gap-2 transition delay-300 focus:outline-none"
            aria-label="Toggle menu"
          >
            <FaBars className="text-lg" />
          </button>
        </div>

        {/* Mobile Sidebar */}
        {isSidebarOpen && (
          <div className="md:hidden bg-gray-700 text-white p-4 absolute top-16 right-0 w-full shadow-lg z-50 transition delay-300">
            <button
              onClick={toggleSidebar}
              className="w-full mb-3 flex justify-end items-end focus:outline-none"
              aria-label="Close menu"
            >
              <MdClose size={30} color="red" />
            </button>
            <ul className="space-y-4">
              {menuItems.map((item) => (
                <li key={item.id} className="flex items-center gap-2">
                  <Link
                    to={item.href}
                    className="flex items-center gap-2 hover:text-gray-300 transition-colors"
                    onClick={toggleSidebar}
                  >
                    {item.icon}
                    {item.text}
                  </Link>
                </li>
              ))}
              {isAuthenticated ? (
                <>
                  <li className="flex items-center gap-2">
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 hover:text-gray-300 transition-colors"
                      onClick={toggleSidebar}
                    >
                      <IoMdPerson />
                      Profile
                    </Link>
                  </li>
                  <li className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        handleLogout();
                        toggleSidebar();
                      }}
                      className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
                    >
                      <MdLogout />
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <li className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShowLoginModal(true);
                      toggleSidebar();
                    }}
                    className="flex items-center gap-2 hover:text-gray-300 transition-colors"
                  >
                    <IoMdPerson />
                    Login
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Dropdown Menu */}
        {isDropdownOpen && isAuthenticated && (
          <div
            ref={dropdownRef}
            className="absolute top-20 right-10 bg-black text-white shadow-sm rounded-md z-60 p-4 w-52"
          >
            <div className="  ">
              {/* <p className="font-semibold">{userData?.firstname || "User"}</p> */}
              {/* <p className="text-sm text-gray-400 truncate"></p> */}
            </div>
            <ul className="space-y-">
              <li>
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-2 py-2 hover:bg-gray-700 rounded transition-colors"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <IoMdPerson size={18} />
                  Profile
                </Link>
              </li>
              <p className="text-sm text-gray-400 truncate border-b "></p>
              <li>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full text-left px-2 py-2 hover:bg-red-600 rounded text-white transition-colors"
                >
                  <MdLogout size={18} />
                  Logout
                </button>
              </li>
            </ul>
          </div>
        )}
      </header>

      {/* Login Modal */}
      {showLoginModal && (
        <Login
          closeModal={() => setShowLoginModal(false)}
          onLoginSuccess={() => {
            setShowLoginModal(false);
            navigate('/check-in/options');
          }}
        />
      )}
    </>
  );
};

export default Header;
